/**
 * HubLibraryList — workspace-level library index inside the slide-
 * over Hub. Sister to ``HubList.tsx`` but lists ALL libraries in the
 * workspace rather than the docs in one library.
 *
 * Includes:
 *   - Sticky "SWITCH LIBRARY" header with create button
 *   - Search/filter input for quick library discovery
 *   - Library rows with accurate metadata (file count, pending, status)
 *   - Pure presentation for row rendering; parent owns selection
 *
 * Each row renders:
 *   - Scope badge (CSR / CMC / IB / Protocol / General).
 *   - Library name.
 *   - Sub-line: ``${files} files · ${signed | N pending} [· modified]``.
 *   - Right-aligned ``›`` chevron, with a leading ``✓`` for the
 *     currently active library.
 *
 * Whole row is one button — clicking drills into that library
 * (parent flips the slide-over to ``list`` mode for the picked library).
 *
 * Empty state mirrors LibraryQuickPicker's empty hint text.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useMemo, useState } from "react";
import type { Library, Workspace } from "../../../types";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../../tokens";

export interface HubLibraryListProps {
  /** All libraries in the writer's workspace. */
  libraries: Library[];
  /** The currently active library, if any — its row gets a brand-
   *  tinted background + a leading ``✓`` next to the chevron. */
  activeLibraryId: string | null;
  /** Click on a row body → select the library for the main chat flow. */
  onPickLibrary: (libraryId: string) => void;
  /** Click on the right chevron → inspect the library inside the Hub. */
  onInspectLibrary: (libraryId: string) => void;
  /** Create new library action — typically opens the slide-over in
   *  ``creating`` mode. */
  onCreateLibrary?: () => void;
  /** Workspaces available to the writer. When provided (2+), a switcher
   *  renders atop the header so the writer can change workspace without
   *  leaving the picker. Omit to hide the switcher entirely. */
  workspaces?: Workspace[];
  /** The active workspace id — drives the switcher's selected option. */
  selectedWorkspaceId?: string | null;
  /** Fires when the writer picks a different workspace. The parent
   *  persists it and cascades the library re-fetch (the ``libraries``
   *  prop updates in place while this list stays mounted). */
  onSelectWorkspace?: (workspaceId: string) => void;
}

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: surfaceColors.paper,
  },
  // Sticky header section — stays visible while scrolling
  header: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: surfaceColors.paper,
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: surfaceColors.line,
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    flexShrink: 0,
  },
  // Workspace switcher — sits above the "SWITCH LIBRARY" row.
  workspaceRow: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  workspaceLabel: {
    fontSize: "9px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    userSelect: "none",
  },
  workspaceSelect: {
    width: "100%",
    padding: "6px 8px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: fonts.body,
    color: inkColors.ink,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "3px",
    backgroundColor: surfaceColors.paperDim,
    cursor: "pointer",
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-1px",
    },
  },
  workspaceStatic: {
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: fonts.body,
    color: inkColors.ink2,
    padding: "2px 0",
  },
  divider: {
    height: "1px",
    backgroundColor: surfaceColors.line,
    margin: "2px 0",
  },
  // "SWITCH LIBRARY" title + create button row
  headerTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  headerTitle: {
    fontSize: "9px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    userSelect: "none",
  },
  createBtn: {
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    minWidth: "24px",
    minHeight: "24px",
    color: brandColors.primary,
    fontSize: "16px",
    lineHeight: 1,
    borderRadius: "3px",
    fontFamily: fonts.body,
    ":hover": {
      backgroundColor: brandColors.light,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-1px",
    },
  },
  // Search input field
  searchInput: {
    width: "100%",
    padding: "6px 8px",
    fontSize: "11px",
    fontFamily: fonts.body,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "3px",
    backgroundColor: surfaceColors.paperDim,
    color: inkColors.ink,
  },
  // Scrollable list container
  listContainer: {
    flex: 1,
    overflow: "auto",
    minHeight: 0,
  },
  empty: {
    padding: "30px 24px",
    textAlign: "center",
    fontSize: "11px",
    color: inkColors.ink4,
    fontStyle: "italic",
    fontFamily: fonts.body,
  },
  rowWrap: {
    display: "flex",
    alignItems: "stretch",
    width: "100%",
    borderBottom: `1px solid ${surfaceColors.line}`,
    backgroundColor: "transparent",
    ":hover": {
      backgroundColor: surfaceColors.paperDim,
    },
  },
  rowWrapActive: {
    backgroundColor: brandColors.light,
    ":hover": {
      backgroundColor: brandColors.light,
    },
  },
  rowBody: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
    minWidth: 0,
    padding: "10px 14px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: fonts.body,
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-2px",
    },
  },
  rowChevronBtn: {
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "0 16px",
    minWidth: "44px",
    minHeight: "44px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: inkColors.ink3,
    ":hover": {
      color: inkColors.ink,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-2px",
    },
  },
  scopeBadge: {
    width: "26px",
    height: "20px",
    borderRadius: "3px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "8px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    letterSpacing: "0.3px",
    flexShrink: 0,
  },
  scopeBadgeSigned: {
    backgroundColor: "rgba(38, 223, 155, 0.18)", // statusColors.cool @ 18%
    color: "#0F5132",
  },
  scopeBadgePending: {
    backgroundColor: "rgba(245, 165, 36, 0.18)", // statusColors.warn @ 18%
    color: "#7C2D12",
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
  },
  rowNameRow: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    minWidth: 0,
  },
  rowName: {
    fontSize: "12px",
    fontWeight: 600,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  infoIcon: {
    flexShrink: 0,
    fontSize: "11px",
    lineHeight: 1,
    color: inkColors.ink4,
    cursor: "help",
  },
  rowMeta: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    marginTop: "2px",
  },
  rowTail: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
    color: inkColors.ink3,
  },
  activeCheck: {
    color: brandColors.primary,
    fontSize: "11px",
    lineHeight: 1,
    fontWeight: 700,
  },
  chevron: {
    fontSize: "26px",
    lineHeight: 1,
    color: inkColors.ink3,
  },
});

/**
 * Build metadata string for a library row.
 *
 * Displays: "X file(s) · signed | N pending [· modified DATE]"
 * - If library is signed: "signed · modified DATE" (if recent)
 * - If library has pending docs: "N pending · modified DATE" (if recent)
 *
 * Accurately reflects current state: document_count tracks total files,
 * unverified tracks docs not yet verified (pending review).
 */
function describeLib(lib: Library): string {
  const parts: string[] = [];

  // File count: core metric, always shown
  const fileCount = lib.document_count ?? 0;
  parts.push(`${fileCount} file${fileCount === 1 ? "" : "s"}`);

  // Status: signed (verified) OR pending (unverified docs)
  if (lib.signed) {
    parts.push("signed");
  } else {
    const pendingCount = lib.unverified ?? 0;
    parts.push(`${pendingCount} pending`);
  }

  // Modified timestamp (if available) — helps track recent activity
  if (lib.modified) {
    parts.push(`modified ${lib.modified}`);
  }

  return parts.join(" · ");
}

/**
 * Filter libraries by search term — case-insensitive name match.
 */
function filterLibraries(libraries: Library[], searchTerm: string): Library[] {
  if (!searchTerm.trim()) return libraries;

  const lowerSearchTerm = searchTerm.toLowerCase();
  return libraries.filter((lib) => lib.name.toLowerCase().includes(lowerSearchTerm));
}

export const HubLibraryList: React.FC<HubLibraryListProps> = ({
  libraries,
  activeLibraryId,
  onPickLibrary,
  onInspectLibrary,
  onCreateLibrary,
  workspaces,
  selectedWorkspaceId,
  onSelectWorkspace,
}) => {
  const styles = useStyles();
  const [searchTerm, setSearchTerm] = useState("");

  // Memoize filtered libraries to avoid unnecessary re-renders
  const filteredLibraries = useMemo(() => filterLibraries(libraries, searchTerm), [libraries, searchTerm]);

  // Workspace switcher — shown whenever the parent supplies workspaces
  // and a change handler. A single workspace renders as static text
  // (nothing to switch to); 2+ render a dropdown that re-fetches the
  // library list on change. Rendered even when the current workspace
  // has zero libraries, so the writer can switch away from an empty one.
  const showWorkspaceSwitcher = !!workspaces && workspaces.length > 0 && !!onSelectWorkspace;
  const activeWorkspace = workspaces?.find((w) => w.id === selectedWorkspaceId) ?? null;
  const workspaceSwitcher = showWorkspaceSwitcher ? (
    <div className={styles.workspaceRow} data-testid="hub-workspace-switcher">
      <div className={styles.workspaceLabel}>Workspace</div>
      {workspaces && workspaces.length > 1 ? (
        <select
          className={styles.workspaceSelect}
          value={selectedWorkspaceId ?? ""}
          onChange={(e) => {
            const id = e.currentTarget.value;
            if (id && id !== selectedWorkspaceId) onSelectWorkspace?.(id);
          }}
          data-testid="hub-workspace-select"
          aria-label="Switch workspace"
        >
          {workspaces.map((ws) => (
            <option key={ws.id} value={ws.id}>
              {ws.name}
            </option>
          ))}
        </select>
      ) : (
        <div className={styles.workspaceStatic}>{activeWorkspace?.name ?? "—"}</div>
      )}
      <div className={styles.divider} />
    </div>
  ) : null;

  return (
    <div className={styles.root} data-testid="hub-library-list">
      {/* Sticky header with workspace switcher + title + create + search */}
      <div className={styles.header}>
        {workspaceSwitcher}
        <div className={styles.headerTop}>
          <div className={styles.headerTitle}>Switch Library</div>
          {onCreateLibrary && (
            <button
              type="button"
              className={styles.createBtn}
              onClick={onCreateLibrary}
              aria-label="Create new library"
              data-testid="hub-library-create"
              title="Create new library"
            >
              +
            </button>
          )}
        </div>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search libraries…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
          data-testid="hub-library-search"
          aria-label="Search libraries"
        />
      </div>

      {/* Scrollable list of libraries */}
      <div className={styles.listContainer}>
        {filteredLibraries.length === 0 ? (
          <div className={styles.empty} data-testid="hub-library-list-empty">
            {searchTerm ? "No libraries match your search." : "No libraries in this workspace yet."}
          </div>
        ) : (
          filteredLibraries.map((lib) => {
            const isActive = lib.id === activeLibraryId;
            const scopeKey = lib.signed ? "signed" : "pending";
            return (
              <div
                key={lib.id}
                className={mergeClasses(styles.rowWrap, isActive && styles.rowWrapActive)}
                data-testid={`hub-library-list-row-${lib.id}`}
              >
                <button
                  type="button"
                  onClick={() => onPickLibrary(lib.id)}
                  className={styles.rowBody}
                  aria-current={isActive ? "true" : undefined}
                  data-testid={`hub-library-list-row-body-${lib.id}`}
                >
                  <span
                    className={mergeClasses(
                      styles.scopeBadge,
                      scopeKey === "signed" ? styles.scopeBadgeSigned : styles.scopeBadgePending,
                    )}
                  >
                    {lib.scope ?? "—"}
                  </span>
                  <div className={styles.rowMain}>
                    <div className={styles.rowNameRow}>
                      <span className={styles.rowName}>{lib.name}</span>
                      {lib.description && (
                        <span
                          className={styles.infoIcon}
                          title={lib.description}
                          aria-label={`Description: ${lib.description}`}
                          data-testid={`hub-library-list-info-${lib.id}`}
                        >
                          ⓘ
                        </span>
                      )}
                    </div>
                    <div className={styles.rowMeta}>{describeLib(lib)}</div>
                  </div>
                  <span className={styles.rowTail} aria-hidden="true">
                    {isActive && <span className={styles.activeCheck}>✓</span>}
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={`Inspect ${lib.name}`}
                  data-testid={`hub-library-list-inspect-${lib.id}`}
                  className={styles.rowChevronBtn}
                  onClick={() => onInspectLibrary(lib.id)}
                >
                  <span className={styles.chevron}>›</span>
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default HubLibraryList;
